import { companiesWords } from "@/config/companies";
import { buildedSiteDataProps } from "@/templates/types";
import "react-loading-skeleton/dist/skeleton.css";

export const parseTextWithEnviromentVariables = (
  text: string,
  data: buildedSiteDataProps,
  company: string
) => {
  let result = text;
  for (const key in data) {
    const pattern = `{{${key}}}`;
    if (result && result.includes(pattern)) {
      const rgx = new RegExp(pattern, "g");

      // Verificar si la clave `key_${company}` existe en el objeto `data`
      const dynamicKey = `${key}_${company}` as keyof typeof data;
      const replacementValue =
        company && key !== "registry_city" && dynamicKey in data
          ? data[dynamicKey] // Si existe, usar `key_${company}`
          : data[key as keyof typeof data]; // Si no, usar `key`

      result = result.replace(rgx, String(replacementValue));
    }
  }
  return result;
};

export const parseTextWithCompanyTextData = (text: string, company: string) => {
  const companyData = companiesWords[company];

  function replacePlaceholder(match: string, key: string) {
    const typedKey = key as keyof typeof companyData;
    return companyData[typedKey] ?? match; // If it does not exist, return the original placeholder
  }

  return text.replace(/{{(.*?)}}/g, replacePlaceholder);
};

export const generateMerchantsList = (
  companiesId: string[],
  companies: string[],
  textKey: string,
  buildedSiteData: buildedSiteDataProps,
  isSiteNegotiatingNewPsp: boolean
) => {
  if (!buildedSiteData) return;
  if (!companies) return;

  if (isSiteNegotiatingNewPsp) {
    const merchantsListText = `<ul>${companiesId
      ?.map((company) => {
        const envText = getEnviromentVariable(textKey, buildedSiteData);

        // Ensure that `envText` is a string. If it is `undefined`, use an empty string by default.
        const parsedText = parseTextWithCompanyTextData(
          typeof envText === "string" ? envText : "",
          company
        );
        return `<li>${parsedText || ""}</li>`;
      })
      .join("")}</ul>`;

    return merchantsListText;
  }

  const merchantsList = `<ul>${companies
    ?.map((company) => {
      const envText = getEnviromentVariable(textKey, buildedSiteData);

      // Asegurarnos de que `envText` sea una cadena. Si es `undefined`, usar una cadena vacía por defecto
      const parsedText = parseTextWithEnviromentVariables(
        typeof envText === "string" ? envText : "",
        buildedSiteData,
        company
      );

      return `<li>${parsedText || ""}</li>`;
    })
    .join("")}</ul>`;

  return merchantsList;
};

export const getEnviromentVariable = (key: string, data: buildedSiteDataProps) => {
  return typeof data[key as keyof typeof data] === "string" || data[key as keyof typeof data]
    ? data[key as keyof typeof data]
    : `<span class="react-loading-skeleton" style="width:150px">‌</span>`;
};

export default function variableAdder(content: string, data: string) {
  if (content !== null) {
    let cont = content;
    let replace = "{{([^}]+)}}";
    let re = new RegExp(replace, "g");
    cont = cont.replace(re, data);
    return cont;
  }
}

export const windowIsExist = typeof window !== "undefined";

export const getDefaultUrl = () => (windowIsExist ? window.location.href : "");

export const getUrlBodyClass = (pathName: string): string => {
  const clean = pathName.replace(/^\//, "");
  // Clean language from bodyClass
  const noLang = clean.replace(/^es\//, "");
  const bodyClass = noLang ? noLang.replace(/\//g, "-") : "home";

  return bodyClass;
};

export const getUrlParams = (url: string = getDefaultUrl()): Record<string, string> => {
  const params: Record<string, string> = {};

  if (windowIsExist) {
    url.replace(/[?&]+([^=&]+)=([^&]*)/gi, (_match, key, value) => {
      params[decodeURIComponent(key)] = decodeURIComponent(value);
      return ""; // required by .replace
    });
  }

  return params;
};

export const getParamInUrl = (key: string, url: string = getDefaultUrl()): string | undefined => {
  return getUrlParams(url)[key];
};

export const scrollBehaviorTo = (y = 0) => {
  const supportsNativeSmoothScroll = "scrollBehavior" in document.documentElement.style;

  if (supportsNativeSmoothScroll) {
    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  } else {
    window.scrollTo(0, y);
  }
};

export const scrollY = () => {
  const supportPageOffset = window.scrollX !== undefined;
  const isCSS1Compat = (document.compatMode || "") === "CSS1Compat";
  return supportPageOffset
    ? window.scrollY
    : isCSS1Compat
      ? document.documentElement.scrollTop
      : document.body.scrollTop;
};

export const isHTML = (str: string) => {
  if (typeof document !== "undefined") {
    let a = document.createElement("div");
    a.innerHTML = str;

    for (let c = a.childNodes, i = c.length; i--; ) {
      if (c[i].nodeType === 1) return true;
    }

    return false;
  }
};

export const isInAnIframe = () => {
  if (windowIsExist) {
    return window.location !== window.parent.location;
  }

  return false;
};

export const arrayToQueryParams = (paramsArray: { [key: string]: string }[]) => {
  let queryParams = "";

  paramsArray.forEach((item) => {
    const key = Object.keys(item)[0]; // Get the first key of the object
    const value = item[key]; // Get the value associated with the key

    if (queryParams === "") {
      queryParams = "?";
    } else {
      queryParams += "&";
    }

    queryParams += `${key}=${value}`;
  });
  return queryParams;
};

export const delay = (ms = 1000) => {
  return new Promise((resolve) => setTimeout(() => resolve(true), ms));
};

export const toCammelCase = (str: string) => {
  const camelCased = str.toLowerCase().replace(/[-\s]+(.)/g, (_, char) => char.toUpperCase());

  return camelCased
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
};

export const toCamelCaseStyles = (styleObj: Record<string, any>) => {
  const newObj: Record<string, any> = {};

  Object.keys(styleObj).forEach((key) => {
    const camelKey = key.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
    newObj[camelKey] = styleObj[key];
  });

  return newObj;
};

export const parseString = (title: string, key: string, replaceString: string) => {
  let parsedTitle = title;

  const pattern = `{${key}}`;
  const rgx = new RegExp(pattern, "g");
  parsedTitle = title.replace(rgx, replaceString);

  return parsedTitle;
};

export const buildPassportCardData = (passportCard: any, contentfullData: any) => {
  if (typeof passportCard === "object" && contentfullData && Array.isArray(contentfullData)) {
    const data = contentfullData.find((item) =>
      [item.slug, `/${item.slug}`].includes(passportCard.slug)
    );

    if (typeof data === "object" && data.governmentPdf) {
      return {
        ...passportCard,
        governmentPdf: data.governmentPdf,
      };
    } else {
      return passportCard;
    }
  }

  return passportCard;
};

export const cleanHtmlTags = (str: string) => {
  return str.replace(/<[^>]*>/g, "");
};

export const booleanToString = (value: boolean) => {
  return value ? "1" : "0";
};

export const isIEBrowser = () => {
  const ua = window.navigator.userAgent;
  const isIE = /MSIE|Trident/.test(ua);
  return isIE;
};

export function getClientApiUrl(): string {
  let apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL || "localhost";
  if (process.env.NEXT_PUBLIC_ENV === "prod" && typeof window !== "undefined") {
    const domain = window.location.hostname.replace("www.", "");
    apiUrl = `https://api.${domain}/api`;
    console.log("getClientApiUrl domain:", apiUrl);
  }

  return apiUrl;
}
